import { useQuery } from '@tanstack/react-query';
import { parseISO } from 'date-fns';

import { queries } from '../../common/api/queryKeys/queries';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { translate } from '../../common/helpers/translate.helper';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { createOverlay } from '../../common/Overlay/Overlay';
import { OverlayHeader } from '../../common/Overlay/OverlayHeader';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import { toDemandDefinitionState } from '../toDemandDefinitionState';
import type { DemandDefinition } from '../types/DemandDefinition';

import { DemandDefinitionFlow } from './DemandDefinitionFlow';

interface DemandDefinitionEditOverlayProps {
    definitionId: string;
    onClose: (customerGroupId?: string) => void;
    substep: string;
}

const toEditableDefinition = (definition: DemandDefinition) => ({
    state: toDemandDefinitionState(definition),
    dataBasis: definition.dataBasis,
    dateFrozen: definition.dateFrozen ? parseISO(definition.dateFrozen) : undefined,
});

export const DemandDefinitionEditOverlay = ({ definitionId, onClose, substep }: DemandDefinitionEditOverlayProps) => {
    const definitionQuery = useQuery({
        ...queries.demandDefinitions.detail(definitionId),
        select: toEditableDefinition,
    });
    const overlayTitle = translate('demandDefinition.editDemandDefinition');

    if (definitionQuery.isSuccess) {
        return (
            <DemandDefinitionFlow
                definitionId={definitionId}
                initialDemandDefinition={definitionQuery.data.state}
                dataBasis={definitionQuery.data.dataBasis}
                dateFrozen={definitionQuery.data.dateFrozen}
                onClose={onClose}
                overlayTitle={overlayTitle}
                substep={substep}
            />
        );
    }

    return createOverlay(
        <OverlayHeader title={overlayTitle} onClose={onClose} maxContentWidth={theme.maxContentWidths.wide}>
            {definitionQuery.isPending ? (
                <Box height="100%" display="flex" alignItems="center" justifyContent="center">
                    <LoadingIndicator />
                </Box>
            ) : null}
            {definitionQuery.isError ? <AdvancedError variant="fluid" /> : null}
        </OverlayHeader>
    );
};
